/**
 * Lightweight in-house analytics client (SCRUM-131).
 *
 * Queues events in memory and flushes them to POST /api/analytics/events
 * every FLUSH_INTERVAL_MS or when the queue reaches MAX_QUEUE_SIZE.
 * On page hide (tab close / navigation away) uses navigator.sendBeacon
 * as a best-effort fallback.
 *
 * Respects navigator.doNotTrack === '1' — all calls become no-ops.
 */

// --- Configuration ---
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE_SIZE = 20;
const ENDPOINT = '/api/analytics/events';

// --- State ---
let queue = [];
// eslint-disable-next-line no-unused-vars
let flushTimer = null;
let initialized = false;
let sessionId = null;

// --- DNT check ---
function isDNT() {
  if (typeof navigator === 'undefined') return false;
  return navigator.doNotTrack === '1' || navigator.doNotTrack === 'yes';
}

// --- Session ID (per page load, not persisted) ---
function getSessionId() {
  if (!sessionId) {
    sessionId = crypto.randomUUID ? crypto.randomUUID() : _fallbackUUID();
  }
  return sessionId;
}

function _fallbackUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// --- Core ---

function enqueue(eventName, category, properties = null) {
  if (isDNT()) return;
  queue.push({
    event_name: eventName,
    category,
    properties,
    page_url: typeof window !== 'undefined' ? window.location.pathname : null,
    session_id: getSessionId(),
  });
  if (queue.length >= MAX_QUEUE_SIZE) {
    flush();
  }
}

function flush() {
  if (queue.length === 0) return;

  // Don't flush on public/unauthenticated routes — backend will reject anyway
  // and the CRA proxy would log noisy ECONNREFUSED errors in local dev.
  const PUBLIC_PATHS = ['/demo', '/verify-email', '/reset-password'];
  const currentPath = (typeof window !== 'undefined' ? window.location.pathname : '').replace(/\/$/, '');
  if (PUBLIC_PATHS.includes(currentPath) || currentPath.startsWith('/shared/')) {
    queue = []; // discard silently
    return;
  }

  const batch = queue.splice(0);

  // Use fetch directly so the Axios 401 interceptor never fires for analytics.
  fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ events: batch }),
  }).catch(() => {});
}

/**
 * Beacon fallback for visibilitychange: hidden / pagehide.
 * navigator.sendBeacon doesn't carry cookies on all browsers,
 * but it's a best-effort last-chance flush.
 */
function beaconFlush() {
  if (queue.length === 0) return;
  const batch = queue.splice(0);
  try {
    const blob = new Blob([JSON.stringify({ events: batch })], {
      type: 'application/json',
    });
    navigator.sendBeacon(ENDPOINT, blob);
  } catch {
    // Silently drop.
  }
}

// --- Public API ---

/**
 * Initialize the analytics module. Call once on app mount.
 * Sets up the periodic flush timer and page-hide listener.
 */
export function initAnalytics() {
  if (initialized || isDNT()) return;
  initialized = true;

  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        beaconFlush();
      }
    });
  }
}

/**
 * Track a page view. Typically called on route change.
 */
export function trackPageView(pathname) {
  enqueue('page.view', 'page_view', { path: pathname });
}

/**
 * Track a user action (button click, form submit, etc.).
 */
export function trackAction(name, properties = {}) {
  enqueue(name, 'action', properties);
}

/**
 * Force an immediate flush (e.g. before logout).
 */
export { flush as flushAnalytics };
